import { LanguageMenu, MerchantSwitchMenu } from '@components'
import cn from '@integrations/classnames'
import { type BaseSyntheticEvent, useCallback, useEffect, useMemo, useState } from '@integrations/react'
import { NavLink } from '@integrations/routing'
import {
  isOwnerOrAdministrator,
  LoginStatuses,
  paths,
  selectCurrentUserRole,
  useAppSelector,
  useAuth,
  useLogoutQry,
  useMerchantQry
} from 'mmfintech-backend-api'
import { tr, useWindowSize } from 'mmfintech-commons'
import AccountMenu from './AccountMenu'
import BrandLogo from './BrandLogo'
import styles from './Header.module.scss'
import MainMenuItems from './MainMenuItems'
import UserMenuItems from './UserMenuItems'

const MAX_MOBILE_WIDTH = 1150

function Header() {
  const [opened, setOpened] = useState<boolean>(false)

  const userStatus = useAuth()
  const customerRole = useAppSelector(selectCurrentUserRole)

  const { merchant } = useMerchantQry()

  const [width] = useWindowSize()
  const { logout } = useLogoutQry()

  const isLoggedIn: boolean = useMemo(() => userStatus === LoginStatuses.LOGGED_IN, [userStatus])

  const isDevelopersMenuVisible: boolean = useMemo(
    () => isOwnerOrAdministrator(customerRole) && merchant?.apiEnabled,
    [customerRole, merchant]
  )
  const isForexMenuVisible: boolean = useMemo(
    () => isOwnerOrAdministrator(customerRole) && merchant?.forexEnabled,
    [customerRole, merchant]
  )
  const isReferralMenuVisible: boolean = useMemo(
    () => isOwnerOrAdministrator(customerRole) && Boolean(merchant?.capabilities?.allowReferrals),
    [customerRole, merchant]
  )
  const isHamburgerVisible: boolean = useMemo(() => width < MAX_MOBILE_WIDTH, [width])

  const hideMenu = useCallback(() => setOpened(false), [])
  const toggleMenu = useCallback(() => setOpened((prevState) => !prevState), [])

  function handleLogoutClick(e: BaseSyntheticEvent) {
    e.preventDefault()
    hideMenu()
    void logout()
    return false
  }

  useEffect(() => {
    width > MAX_MOBILE_WIDTH && hideMenu()
  }, [width, hideMenu])

  useEffect(() => {
    if (document) {
      const body = document.getElementsByTagName('body').item(0)
      if (body) {
        if (opened) {
          body.classList.add('menu-opened')
        } else {
          body.classList.remove('menu-opened')
        }
      }
    }
  }, [opened])

  return (
    <div className={cn(styles['header-wrapper'], 'header-wrapper')}>
      {isHamburgerVisible && (
        // Always mounted so it can fade out as well as in; hidden and click-through while the menu is closed
        <div
          className={cn(styles['overlay'], 'overlay', { [styles['opened']]: opened, opened })}
          onClick={hideMenu}
          aria-hidden='true'
        />
      )}

      <nav className={cn(styles['header-container'], 'header-container')}>
        <BrandLogo isLoggedIn={isLoggedIn} />

        {isHamburgerVisible ? (
          <div className={cn(styles['header-inner'], 'header-inner')}>
            <div className={cn(styles['hamburger-menu'], 'hamburger-menu')}>
              {/* The three bars turn into an X while the menu is open, and stay above the panel as its close control */}
              <button
                type='button'
                className={cn(styles['hamburger-icon'], 'hamburger-icon', { [styles['opened']]: opened, opened })}
                onClick={toggleMenu}
                aria-label={
                  opened ? tr('FRONTEND.BUTTONS.CLOSE', 'Close') : tr('FRONTEND.HEADER.OPEN_MENU', 'Open menu')
                }
                aria-expanded={opened}
                data-test='menu-toggle-button'
              >
                <span className={cn('menu-icon')} />
              </button>
              <nav
                className={cn(styles['hamburger-content'], 'hamburger-content', { [styles['opened']]: opened, opened })}
              >
                <LanguageMenu hamburger showIcons />
                {isLoggedIn ? (
                  <>
                    <MerchantSwitchMenu hamburger />
                    <MainMenuItems
                      hideMenu={hideMenu}
                      isForexMenuVisible={isForexMenuVisible}
                      isDevelopersMenuVisible={isDevelopersMenuVisible}
                      isReferralMenuVisible={isReferralMenuVisible}
                      customerRole={customerRole}
                    />
                    <UserMenuItems onClick={hideMenu} />
                    <NavLink to='#' onClick={handleLogoutClick}>
                      {tr('FRONTEND.HEADER.LOGOUT', 'Log out')}
                    </NavLink>
                  </>
                ) : (
                  <>
                    <NavLink to={paths.login()} onClick={hideMenu}>
                      {tr('FRONTEND.HEADER.LOGIN', 'Log in')}
                    </NavLink>
                    <NavLink to={paths.signUp()} onClick={hideMenu}>
                      {tr('FRONTEND.HEADER.SIGNUP', 'Sign Up Free')}
                    </NavLink>
                  </>
                )}
              </nav>
            </div>
          </div>
        ) : (
          <>
            {isLoggedIn && (
              <div className={cn(styles['header-content'], 'header-content')}>
                <MainMenuItems
                  hideMenu={hideMenu}
                  isForexMenuVisible={isForexMenuVisible}
                  isDevelopersMenuVisible={isDevelopersMenuVisible}
                  isReferralMenuVisible={isReferralMenuVisible}
                  customerRole={customerRole}
                />
              </div>
            )}
            <div className={cn(styles['header-inner'], 'header-inner')}>
              {isLoggedIn && <AccountMenu />}
              <LanguageMenu showIcons pill />
            </div>
          </>
        )}
      </nav>
    </div>
  )
}

export default Header
